import { init as initCriticReviews } from '@theme/critic-reviews';

const SECTION_KEY = 'accolades-card-data';
const SETUP_KEY = Symbol('accolades-card');

/** @type {Map<string, { imageHtml: string; reviewsHtml: string; productUrl: string }>} */
const cache = new Map();

/** @param {HTMLElement} card */
function setupCard(card) {
  const c = /** @type {any} */ (card);
  if (c[SETUP_KEY]) return;
  c[SETUP_KEY] = true;

  const tabs = /** @type {HTMLButtonElement[]} */ (
    Array.from(card.querySelectorAll('[data-accolades-tabs] button[role="tab"]'))
  );
  if (tabs.length === 0) return;

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => activateTab(card, tabs, tab, { focus: false }));
    tab.addEventListener('keydown', (event) => onTabKeydown(event, card, tabs, tab));
  });

  const initialActive = tabs.find((t) => t.classList.contains('accolades-card__tab--active')) ?? tabs[0];
  if (initialActive && initialActive.dataset.productUrl) {
    fetchTab(card, initialActive);
  }
}

/**
 * @param {KeyboardEvent} event
 * @param {HTMLElement} card
 * @param {HTMLButtonElement[]} tabs
 * @param {HTMLButtonElement} currentTab
 */
function onTabKeydown(event, card, tabs, currentTab) {
  const idx = tabs.indexOf(currentTab);
  /** @type {HTMLButtonElement | null} */
  let target = null;

  switch (event.key) {
    case 'ArrowLeft':
      target = tabs[(idx - 1 + tabs.length) % tabs.length] ?? null;
      break;
    case 'ArrowRight':
      target = tabs[(idx + 1) % tabs.length] ?? null;
      break;
    case 'Home':
      target = tabs[0] ?? null;
      break;
    case 'End':
      target = tabs[tabs.length - 1] ?? null;
      break;
    default:
      return;
  }

  event.preventDefault();
  activateTab(card, tabs, target, { focus: true });
}

/**
 * @param {HTMLElement} card
 * @param {HTMLButtonElement[]} tabs
 * @param {HTMLButtonElement | null} tab
 * @param {{ focus: boolean }} options
 */
function activateTab(card, tabs, tab, { focus }) {
  if (!tab) return;

  tabs.forEach((t) => {
    const active = t === tab;
    t.classList.toggle('accolades-card__tab--active', active);
    t.setAttribute('aria-selected', active ? 'true' : 'false');
    t.setAttribute('tabindex', active ? '0' : '-1');
  });

  if (focus) tab.focus();

  const panel = card.querySelector('[data-accolades-details]');
  if (panel) panel.setAttribute('aria-labelledby', tab.id);

  const productUrl = tab.dataset.productUrl;
  if (!productUrl) return;

  if (cache.has(productUrl)) {
    applyData(card, cache.get(productUrl));
    return;
  }

  card.setAttribute('aria-busy', 'true');
  fetchTab(card, tab).finally(() => card.setAttribute('aria-busy', 'false'));
}

/**
 * @param {HTMLElement} card
 * @param {{ imageHtml?: string; reviewsHtml?: string; productUrl?: string } | undefined} data
 */
function applyData(card, data) {
  if (!data) return;

  const imageContainer = card.querySelector('[data-accolades-image]');
  const reviewsContainer = card.querySelector('[data-accolades-details]');
  const link = /** @type {HTMLAnchorElement | null} */ (card.querySelector('[data-accolades-link]'));
  const titleLink = /** @type {HTMLAnchorElement | null} */ (card.querySelector('[data-accolades-title-link]'));

  if (imageContainer && data.imageHtml != null) {
    imageContainer.innerHTML = data.imageHtml;
  }
  if (reviewsContainer && data.reviewsHtml != null) {
    reviewsContainer.innerHTML = data.reviewsHtml;
    initCriticReviews();
  }
  if (data.productUrl) {
    if (link) link.href = data.productUrl;
    if (titleLink) titleLink.href = data.productUrl;
  }
}

/**
 * @param {HTMLElement} card
 * @param {HTMLButtonElement} tab
 */
async function fetchTab(card, tab) {
  const productUrl = tab.dataset.productUrl;
  if (!productUrl) return;

  const url = new URL(productUrl, window.location.origin);
  url.searchParams.set('sections', SECTION_KEY);

  try {
    const res = await fetch(url.toString());
    const json = await res.json();
    const html = json[SECTION_KEY];
    if (!html) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const data = {
      imageHtml: doc.querySelector('[data-accolades-image]')?.innerHTML ?? '',
      reviewsHtml: doc.querySelector('[data-accolades-reviews]')?.innerHTML ?? '',
      productUrl: doc.querySelector('[data-accolades-url]')?.textContent?.trim() || productUrl,
    };

    cache.set(productUrl, data);
    applyData(card, data);
  } catch (err) {
    console.error('[accolades-card] fetch failed:', err);
  }
}

function init() {
  document.querySelectorAll('[data-accolades-card]').forEach((el) => setupCard(/** @type {HTMLElement} */ (el)));
}

const win = /** @type {any} */ (window);
if (!win.__accoladesCardBound) {
  win.__accoladesCardBound = true;
  document.addEventListener('shopify:section:load', init);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
